import { ReactNode } from 'react';
import {
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormHelperText,
  forwardRef,
  Select as ChakraSelect,
  SelectProps as ChakraSelectProps,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import Box from '../Box/Box';
import Icon from '../Icon/Icon';
import ProgressSpinner from '../ProgressSpinner/ProgressSpinner';
import FormLabel, { FormLabelProps } from '../Form/FormLabel';
import { TooltipProps } from '../Tooltip/Tooltip';
import { getDataAttributes } from '../../utils/utils';

export interface SelectProps extends Omit<FormControlProps, 'label' | 'onBlur' | 'onChange'> {
  badge?: FormLabelProps['badge'];
  errorText?: string;
  helperText?: ReactNode;
  infoTooltipLabel?: string;
  infoTooltipProps?: TooltipProps;
  isLoading?: boolean;
  isWarning?: boolean;
  label?: string;
  name?: string;
  onBlur?: ChakraSelectProps['onBlur'];
  onChange?: ChakraSelectProps['onChange'];
  placeholder?: ChakraSelectProps['placeholder'];
  size?: 'lg' | 'md';
  value?: ChakraSelectProps['value'];
}

const Select = forwardRef<SelectProps, 'div'>((props, ref) => {
  const {
    badge,
    children,
    defaultValue,
    errorText,
    helperText,
    infoTooltipLabel,
    infoTooltipProps,
    isDisabled,
    isInvalid,
    isLoading,
    isReadOnly,
    isWarning,
    label,
    name,
    onBlur,
    onChange,
    placeholder,
    size = 'lg',
    value,
    ...rest
  } = props;
  const isSelectInvalid = isInvalid || !!errorText;

  const formControlProps = {
    isDisabled: isDisabled || isLoading || isReadOnly,
    isInvalid: isSelectInvalid,
    isReadOnly,
    ...rest,
  };
  const selectProperties: ChakraSelectProps = {
    defaultValue,
    icon: isLoading ? <ProgressSpinner marginEnd="4" /> : <Icon fontSize="24" name="SelectChevron" />,
    name,
    onBlur,
    onChange,
    size,
    value,
  };

  const dataAttributes = getDataAttributes(props);

  if (placeholder) {
    if ('value' in props) {
      selectProperties.value = selectProperties.value ?? '';
    } else {
      selectProperties.defaultValue = props.defaultValue ?? '';
    }
  }

  const iconSize = size === 'lg' ? '24' : '16';
  const style = useMultiStyleConfig('Select', {
    hasStateIcon: isSelectInvalid || isWarning,
    isPlaceholderShown: !!placeholder && !value,
  });

  return (
    <FormControl {...formControlProps}>
      <FormLabel badge={badge} infoTooltipLabel={infoTooltipLabel} infoTooltipProps={infoTooltipProps}>
        {label}
      </FormLabel>
      <Box sx={style.selectContainer}>
        <ChakraSelect ref={ref} {...selectProperties} {...dataAttributes} sx={style.field}>
          {placeholder && (
            <option disabled value="">
              {placeholder}
            </option>
          )}
          {children}
        </ChakraSelect>
        {!isSelectInvalid && isWarning && (
          <Icon aria-hidden="true" name="WarningYellow" size={iconSize} sx={style.stateIcon} />
        )}
        {isSelectInvalid && (
          <Icon
            aria-hidden="true"
            color="icon/negative"
            name="ErrorCircleFilled"
            size={iconSize}
            sx={style.stateIcon}
          />
        )}
      </Box>
      {errorText && <FormErrorMessage as="p">{errorText}</FormErrorMessage>}
      {!errorText && helperText && (
        <FormHelperText as="p" data-disabled={isDisabled || undefined}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
});

export default Select;
